import {ApplicationController} from "../support/application-controller";
import { fetchPost, fetchPatch, fetchDelete } from "../support/api-helpers";
import ExternalMappingManager from "./external_mapping_manager";
export default class extends ApplicationController {
  static targets = ["addModal", "editModal", "addForm", "editForm", "paymentDate", "paymentAmount", "paymentStatus", "externalSourceType", "externalSourceId", "paymentNotes", "editPaymentId", "editPaymentAmount", "editPaymentStatus", "editPaymentDate", "editPaymentNotes"];

  connect() {
    ExternalMappingManager.makeFunctionsAvailable();
    this.setDefaultDate();
  }

  setDefaultDate() {
    if (this.hasPaymentDateTarget) {
      const now = new Date();
      now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
      this.paymentDateTarget.value = now.toISOString().slice(0, 16);
    }
  }

  openAddModal() {
    if (this.hasAddModalTarget) {
      this.addModalTarget.style.display = 'block';
    }
  }

  closeAddModal() {
    if (this.hasAddModalTarget) {
      this.addModalTarget.style.display = 'none';
    }
    this.clearAddForm();
  }

  openEditModal(event) {
    const paymentId = event.currentTarget.dataset.paymentId;
    this.fetchPaymentData(paymentId);
  }

  closeEditModal() {
    if (this.hasEditModalTarget) {
      this.editModalTarget.style.display = 'none';
    }
    this.clearEditForm();
  }

  clearAddForm() {
    if (this.hasAddFormTarget) {
      this.addFormTarget.reset();
      this.setDefaultDate();
    }
  }

  clearEditForm() {
    if (this.hasEditFormTarget) {
      this.editFormTarget.reset();
    }
  }

  submitAdd(event) {
    event.preventDefault();

    const formData = {
      amount: this.paymentAmountTarget.value,
      status: this.paymentStatusTarget.value,
      payment_date: this.paymentDateTarget.value,
      external_source_type: this.hasExternalSourceTypeTarget ? this.externalSourceTypeTarget.value : '',
      external_source_id: this.hasExternalSourceIdTarget ? this.externalSourceIdTarget.value : '',
      notes: this.paymentNotesTarget.value
    };

    const { identityId, subscriptionId } = this.getSubscriptionDetails();

    fetchPost({
      url: `/admin/identity/${identityId}/subscriptions/${subscriptionId}/payments`,
      data: formData
    })
    .then(data => {
      this.closeAddModal();
      this.showAlert('Payment created successfully!', 'success');
      setTimeout(() => window.location.reload(), 1000);
    })
    .catch(error => {
      console.error('Error creating payment:', error);
      this.showAlert(`Error creating payment: ${error.message}`, 'error');
    });
  }

  submitEdit(event) {
    event.preventDefault();

    const paymentId = this.editPaymentIdTarget.value;
    const formData = {
      amount: this.editPaymentAmountTarget.value,
      status: this.editPaymentStatusTarget.value,
      payment_date: this.editPaymentDateTarget.value,
      notes: this.editPaymentNotesTarget.value
    };

    const { identityId, subscriptionId } = this.getSubscriptionDetails();

    fetchPatch({
      url: `/admin/identity/${identityId}/subscriptions/${subscriptionId}/payments/${paymentId}`,
      data: formData
    })
    .then(data => {
      this.closeEditModal();
      this.showAlert('Payment updated successfully!', 'success');
      setTimeout(() => window.location.reload(), 1000);
    })
    .catch(error => {
      console.error('Error updating payment:', error);
      this.showAlert(`Error updating payment: ${error.message}`, 'error');
    });
  }

  deletePayment(event) {
    const paymentId = event.currentTarget.dataset.paymentId;

    if (!confirm('Are you sure you want to delete this payment? This action cannot be undone.')) {
      return;
    }

    const { identityId, subscriptionId } = this.getSubscriptionDetails();

    fetchDelete({
      url: `/admin/identity/${identityId}/subscriptions/${subscriptionId}/payments/${paymentId}`
    })
    .then(data => {
      this.showAlert('Payment deleted successfully!', 'success');
      setTimeout(() => window.location.reload(), 1000);
    })
    .catch(error => {
      console.error('Error deleting payment:', error);
      this.showAlert(`Error deleting payment: ${error.message}`, 'error');
    });
  }

  fetchPaymentData(paymentId) {
    const { identityId, subscriptionId } = this.getSubscriptionDetails();

    const csrfToken = document.querySelector('meta[name="csrf-token"]');
    fetch(`/admin/identity/${identityId}/subscriptions/${subscriptionId}/payments/${paymentId}`, {
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken ? csrfToken.content : null,
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Failed to fetch payment data');
      }
      return response.json();
    })
    .then(paymentData => {
      this.populateEditForm(paymentData);
      if (this.hasEditModalTarget) {
        this.editModalTarget.style.display = 'block';
      }
    })
    .catch(error => {
      console.error('Error fetching payment data:', error);
      this.showAlert(`Error loading payment data: ${error.message}`, 'error');
    });
  }

  populateEditForm(paymentData) {
    this.editPaymentIdTarget.value = paymentData.id;
    this.editPaymentAmountTarget.value = paymentData.amount || '';
    this.editPaymentStatusTarget.value = paymentData.payment_status || '';
    this.editPaymentNotesTarget.value = paymentData.notes || '';

    // Format the date for the datetime-local input
    if (paymentData.created_at) {
      const date = new Date(paymentData.created_at);
      date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
      this.editPaymentDateTarget.value = date.toISOString().slice(0, 16);
    }
  }

  getSubscriptionDetails() {
    // Get subscription details from the page URL
    const pathParts = window.location.pathname.split('/');
    return {
      identityId: pathParts[3], // /admin/user/{identity_id}/subscriptions/{subscription_id}
      subscriptionId: pathParts[5]
    };
  }

  showAlert(message, type) {
    const alertDiv = document.createElement('div');
    const bgColor = type === 'success' ? '#d4edda' : '#f8d7da';
    const textColor = type === 'success' ? '#155724' : '#721c24';
    const borderColor = type === 'success' ? '#c3e6cb' : '#f5c6cb';
    const icon = type === 'success' ? '✅' : '⚠️';

    alertDiv.innerHTML = `
      <div style="position:fixed;top:20px;right:20px;z-index:10000;background:${bgColor};color:${textColor};border:1px solid ${borderColor};padding:12px 16px;border-radius:6px;box-shadow:0 4px 12px rgba(0,0,0,0.15);max-width:400px;">
        <div style="display:flex;align-items:center;">
          <span style="margin-right:8px;">${icon}</span>
          <span style="font-size:14px;font-weight:500;">${message}</span>
          <button onclick="this.parentElement.parentElement.remove()" style="margin-left:auto;background:none;border:none;color:${textColor};cursor:pointer;padding:0 4px;">✕</button>
        </div>
      </div>
    `;

    document.body.appendChild(alertDiv);

    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (alertDiv.parentElement) {
        alertDiv.remove();
      }
    }, 5000);
  }

  closeOnBackdrop(event) {
    // Only close if clicking on the modal backdrop, not the modal content
    if (event.target === event.currentTarget) {
      if (this.hasAddModalTarget && event.currentTarget === this.addModalTarget) {
        this.closeAddModal();
      } else if (this.hasEditModalTarget && event.currentTarget === this.editModalTarget) {
        this.closeEditModal();
      }
    }
  }
}
